import axios from "axios";
import { storageKeys } from "@/composables/useExpiredDevices";
import { auth0 } from "@/auth/_globals.js";

const API = import.meta.env.VITE_API;
const API_SUPPORT = import.meta.env.VITE_API_SUPPORT;
const USER = import.meta.env.VITE_USER ?? "user";

type User = {
	username: string;
	fullName: string | null;
	phoneNumber: string | null;
	language: string;
	privileges: Record<string, Privilege[]>;
	isOrganizationAdmin?: boolean;
	roles?: string[];
};

type Privilege = {
	privilegeId: string;
	action: string;
	resource: Resource;
};

type Resource = {
	resourceType: string;
	resourceId: string;
};

export default {
	/**
	 * Check if is a demo user who is logged in
	 */
	isDemo() {
		const loggedUser = this.user();
		return loggedUser
			? loggedUser.username.toLowerCase() === "demo@wattsense.com"
			: false;
	},

	/**
	 * Check if the logged in user is a Wattsense user
	 */
	isWattsense() {
		const loggedUser = this.user();
		return (
			!this.isDemo() &&
			loggedUser?.username?.toLowerCase().endsWith("@wattsense.com")
		);
	},

	/**
	 * Check if logged user is account admin
	 */
	isAccountAdmin() {
		const loggedUser = this.user();
		return loggedUser ? loggedUser.isOrganizationAdmin : false;
	},

	/**
	 * Check if user is read only on all his organizations
	 */
	isReadOnlyUser() {
		const loggedUser = this.user();
		if (!loggedUser) {
			return true;
		}

		for (const [, privileges] of Object.entries(
			loggedUser?.privileges ?? {}
		)) {
			for (const privilege of privileges) {
				const permission = privilege.action;
				if (
					permission.includes("Write") ||
					["*", "Admin", "admin"].includes(permission)
				) {
					return false;
				}
			}
		}

		return true;
	},

	/**
	 * Verify if user is authenticated
	 */
	isAuthenticated() {
		return auth0.isAuthenticated.value;
	},

	/**
	 * Verify if user has SUPPORT role
	 */
	isSupport() {
		const loggedUser = this.user();
		if (!loggedUser) {
			return false;
		}

		return loggedUser.roles?.some(
			(role) => role.toLowerCase() === "support"
		);
	},

	/**
	 * Logout user
	 */
	async logout() {
		this.clearSession();
		await auth0.logout({
			logoutParams: {
				returnTo: window.location.origin
			}
		});
	},

	/**
	 * Removes Tokens
	 */
	clearSession() {
		localStorage.removeItem(USER);
		localStorage.removeItem("last-selected-organizationId");
		localStorage.removeItem(storageKeys.EXPIRING);
		localStorage.removeItem(storageKeys.EXPIRED);
	},

	requestNewPassword(email: string) {
		return axios
			.get(`${API_SUPPORT}/password-reset`, {
				params: {
					username: email
				}
			})
			.then((resp) => resp.data)
			.catch((err) => {
				throw err.response;
			});
	},

	resetPassword(
		token: string,
		email: string,
		password: string,
		password_confirmation: string
	) {
		return axios
			.put(`${API_SUPPORT}/new-password?token=${token}`, {
				username: email,
				password: password,
				passwordConfirm: password_confirmation
			})
			.then((resp) => resp.data)
			.catch((err) => {
				throw err.response;
			});
	},

	getUserInfo() {
		return axios
			.get(`${API}/user`, { params: { showSpinner: false } })
			.then((resp) => {
				const user = resp.data;
				localStorage.setItem(USER, JSON.stringify(user));
				return user;
			})
			.catch((err) => {
				localStorage.removeItem(USER);
				throw err.response;
			});
	},

	user(): User | undefined {
		try {
			const user = localStorage.getItem(USER);
			if (!user) {
				return;
			}
			return JSON.parse(user);
		} catch (error) {
			return;
		}
	},

	updateUser(user) {
		const updateUser = {
			...this.user(),
			...user
		};
		localStorage.setItem(USER, JSON.stringify(updateUser));
	}
};
